import { MusicPaperRecordKeeperServiceClientConfigFactory } from '@amzn/coral-config_music-paper-record-keeper-service';
import {
    MusicPaperRecordKeeperService,
    musicPaperRecordKeeperServiceHttpBindingInterceptor
} from '@amzn/coral_com-amazon-musicpaperrecordkeeperservice';
import {
    CoralClientFactory,
    CoralFetchHandler,
    CoralHandler,
    CoralInterceptor,
    CoralRequest,
    CoralResponse,
    InterceptingHandler
} from '@amzn/ihm-js-coral-client';
import { Aws4HmacSha256Interceptor } from '@amzn/ihm-js-coral-client';

import { Auth } from 'aws-amplify';
import { isNil } from 'lodash';
import { Observable, defer, from, merge, of, throwError } from 'rxjs';
import { catchError, map, mergeMap, take, tap } from 'rxjs/operators';
import { getStage, getUserConfig } from 'src/util/config';

import AuxiliaryRequestInterceptor from '../AuxiliaryRequestInterceptor';

const getQualifier = () => {
    switch (getStage()) {
        case 'personal': // TODO - setup the client config for personal stacks and add permission to APIG resource policy
            return getUserConfig().documentRepositoryCoralConfig ?? 'beta.us-east-1';
        case 'test':
            return 'beta.us-east-1';
        case 'beta':
            return 'beta.us-east-1';
        case 'gamma':
            return 'gamma.us-east-1';
        case 'prod':
            return 'prod.us-east-1';
        default:
            return 'beta.us-east-1';
    }
};

const sigV4Interceptor = new Aws4HmacSha256Interceptor(async () => {
    const credentials = await Auth.currentCredentials();

    return {
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey,
        sessionToken: credentials.sessionToken
    };
});

class CognitoTokenInterceptor extends CoralInterceptor {
    private readonly idToken: Observable<string>;

    constructor() {
        super();
        this.idToken = defer(() =>
            Auth.currentSession().then((session) => {
                return session.getIdToken().getJwtToken();
            })
        );
    }

    intercept(request: CoralRequest, next: CoralHandler): Observable<CoralResponse<unknown>> {
        return this.idToken.pipe(
            take(1),
            mergeMap((token) =>
                next.handle(
                    request.clone({
                        headers: [...request.headers, ['Authorization', token]]
                    })
                )
            )
        );
    }
}

class DualAuthInterceptor extends CoralInterceptor {
    private readonly userPoolInterceptor: CoralInterceptor;
    private readonly sigV4Interceptor: CoralInterceptor;

    constructor() {
        super();

        this.userPoolInterceptor = new CognitoTokenInterceptor();
        this.sigV4Interceptor = sigV4Interceptor;
    }

    intercept(request: CoralRequest, next: CoralHandler): Observable<CoralResponse<unknown>> {
        return this.userPoolInterceptor.intercept(request, next).pipe(
            tap((data) => {
                if (!data.ok) {
                    throw new Error(JSON.stringify(data));
                }
            }),
            catchError(() => {
                return this.sigV4Interceptor.intercept(request, next);
            })
        );
    }
}

function personalStackInterceptors(): CoralInterceptor[] {
    if (getStage() !== 'personal') {
        return [];
    }

    const userConfig = getUserConfig();

    return isNil(userConfig.documentRepositoryEndpoint)
        ? []
        : [
              {
                  intercept: (request: CoralRequest, next: CoralHandler) => {
                      return next.handle(
                          request.clone({
                              url: userConfig.documentRepositoryEndpoint
                          })
                      );
                  }
              }
          ];
}

const clientFactory = new CoralClientFactory(
    [MusicPaperRecordKeeperServiceClientConfigFactory],
    getQualifier(),
    new InterceptingHandler(new CoralFetchHandler(), [
        musicPaperRecordKeeperServiceHttpBindingInterceptor,
        new AuxiliaryRequestInterceptor(),
        new DualAuthInterceptor()
        // new CognitoTokenInterceptor(),
        // sigV4Interceptor
    ]),
    personalStackInterceptors()
);

export const RECORD_KEEPER_TENANT_ID = 'label-cam';

export const recordKeeperClient = clientFactory.get(MusicPaperRecordKeeperService);
